import * as React from 'react'

import { Box } from '~ui/components/layout/Box'
import { H1, H2 } from '~ui/components/typography/Heading/Heading'
import { Text } from '~ui/components/typography/Text/Text'
import { letterSpacingValues } from '~ui/styleGuide/fontStyles'
import { TSingleSpace } from '~ui/styleGuide/spaces'

const defaultHeadlineMarginBottom: TSingleSpace = `largePlus`

type TProps = NoChildren & {
  isMobile?: boolean
  title: React.ReactNode
  subtitle?: React.ReactNode
  noBottomMargin?: boolean
  headingComponent?: `h1` | `h2`
}

/**
 * Title & _optional_ subtitle of the screen.
 *
 * Renders a bottom margin by default, you can turn it off using the `noBottomMargin` prop.
 */
export const ScreenHeadline: React.FC<TProps> = ({
  title,
  subtitle,
  isMobile = false,
  noBottomMargin = false,
  headingComponent = 'h1',
}) => {
  const HeadingComponent = headingComponent === `h1` ? H1 : H2

  const titleMargin = React.useMemo<TSingleSpace>(() => {
    if (subtitle) return `smallMedium`
    if (noBottomMargin) return `none`
    return defaultHeadlineMarginBottom
  }, [noBottomMargin, subtitle])

  return (
    <Box>
      <Box
        margin={{
          top: `none`,
          right: `none`,
          left: `none`,
          bottom: titleMargin,
        }}
      >
        <HeadingComponent
          letterSpacing={letterSpacingValues.tighter}
          lineHeight={1.2}
          fontStyle={`bold`}
          textAlign={`inherit`}
          fontSize={isMobile ? `l28` : `l34`}
        >
          {title}
        </HeadingComponent>
      </Box>

      {subtitle && (
        <Box
          margin={{
            top: `none`,
            right: `none`,
            left: `none`,
            bottom: noBottomMargin ? `none` : defaultHeadlineMarginBottom,
          }}
        >
          <Text contrast={`low`}>{subtitle}</Text>
        </Box>
      )}
    </Box>
  )
}
